import {useEffect, useState} from "react"
import {useQuery} from "@tanstack/react-query"

import type {IdentifiableCustomer} from "./types"

import {api} from "@indebted/api"

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 300
const FAILED_TO_SEARCH = "Failed to search customers"

type CustomerSearchState = {
	results: IdentifiableCustomer[]
	loading: boolean
	error: string
}

type CustomerSearchWireResult = {
	ID: string
	FullName: string
	DOB: string
	ClientName: string
	SystemOfRecord: string
	CollectionHold: boolean
}

function toIdentifiableCustomer(result: CustomerSearchWireResult): IdentifiableCustomer {
	return {
		CustomerID: result.ID,
		Name: result.FullName,
		Client: result.ClientName,
		DOB: result.DOB,
		SystemOfRecord: result.SystemOfRecord,
		CollectionHold: result.CollectionHold,
	}
}

async function searchCustomers(query: string): Promise<IdentifiableCustomer[]> {
	const response = await fetch(api.url(`/customers?q=${encodeURIComponent(query)}`), {method: "GET"})
	// The endpoint answers 404 when nothing matched, which is a result and not a failure.
	if (response.status === 404) return []
	if (!response.ok) throw new Error(FAILED_TO_SEARCH)

	const body = await response.json()
	if (!Array.isArray(body)) throw new Error(FAILED_TO_SEARCH)

	return (body as CustomerSearchWireResult[]).map(toIdentifiableCustomer)
}

function useDebounced(value: string): string {
	const [debounced, setDebounced] = useState(value)

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), DEBOUNCE_MS)
		return () => clearTimeout(timer)
	}, [value])

	return debounced
}

function useCustomerSearch(query: string): CustomerSearchState {
	const trimmed = query.trim()
	const debounced = useDebounced(trimmed)
	const settling = debounced !== trimmed
	const enabled = debounced.length >= MIN_QUERY_LENGTH

	const {data, isFetching, isError} = useQuery({
		queryKey: ["unidentifiedInbox", "customerSearch", debounced],
		queryFn: () => searchCustomers(debounced),
		enabled,
	})

	return {
		results: data ?? [],
		loading: settling || (enabled && isFetching),
		error: isError ? FAILED_TO_SEARCH : "",
	}
}

export {useCustomerSearch, MIN_QUERY_LENGTH}
export type {CustomerSearchState}
